import { trackEvent } from '../lib/analytics';
import { useTrainingSession } from './session';
import { TEAM_ROSTER, SEATS_USED, SEATS_TOTAL } from './fixtures';

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  complete: { label: 'Complete', className: 'tr-badge-done' },
  in_progress: { label: 'In progress', className: 'tr-badge-prog' },
  past_due: { label: 'Past due', className: 'tr-badge-late' },
};

export function TeamDashboard() {
  const { persona } = useTrainingSession();

  if (persona !== 'manager') {
    return (
      <div className="tr-root">
        <div className="tr-wrap tr-appsec">
          <div className="tr-gate">
            <h2>Manager access needed</h2>
            <p>Team training is visible to the manager who bought Pro for the team. Switch to the manager persona above to see it.</p>
          </div>
        </div>
      </div>
    );
  }

  const totalAssigned = TEAM_ROSTER.reduce((s, p) => s + p.assignedModules, 0);
  const totalCompleted = TEAM_ROSTER.reduce((s, p) => s + p.completedModules, 0);
  const pctComplete = Math.round((totalCompleted / totalAssigned) * 100);
  const fullyCaughtUp = TEAM_ROSTER.filter((p) => p.completedModules === p.assignedModules).length;
  const pastDue = TEAM_ROSTER.filter((p) => p.status === 'past_due').length;

  return (
    <div className="tr-root">
      <div className="tr-wrap tr-appsec">
        <div className="tr-mgr-head">
          <div>
            <h1 className="tr-h1">Team training</h1>
            <p className="tr-sub">Who's covered, who isn't, and what you can show for it.</p>
          </div>
          <span className="tr-seats"><b>{SEATS_USED} of {SEATS_TOTAL}</b> seats used</span>
        </div>

        <div className="tr-tiles">
          <div className="tr-tile">
            <div className="tr-k">Assigned training completed</div>
            <div className="tr-v">{pctComplete}%</div>
            <div className="tr-n">Across {TEAM_ROSTER[0]?.assignedModules ?? 0} assigned modules</div>
          </div>
          <div className="tr-tile">
            <div className="tr-k">Fully caught up</div>
            <div className="tr-v">{fullyCaughtUp} of {TEAM_ROSTER.length}</div>
            <div className="tr-n">People who've finished everything assigned</div>
          </div>
          <div className="tr-tile tr-warn">
            <div className="tr-k">Overdue</div>
            <div className="tr-v">{pastDue}</div>
            <div className="tr-n">Chase these before the next review</div>
          </div>
        </div>

        <div className="tr-tblbar">
          <h3>{TEAM_ROSTER.length} people</h3>
          <button
            className="tr-btn tr-btn-primary tr-btn-sm"
            onClick={() => trackEvent('training_assigned', { scope: 'team' })}
          >
            Assign training
          </button>
        </div>

        <table className="tr-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Assigned modules</th>
              <th>Last activity</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {TEAM_ROSTER.map((p) => {
              const badge = STATUS_BADGE[p.status];
              const pct = Math.round((p.completedModules / p.assignedModules) * 100);
              return (
                <tr key={p.id}>
                  <td>
                    <div className="tr-who">
                      <span className="tr-av">{p.initials}</span>
                      <div>
                        <div className="tr-nm">{p.name}</div>
                        <div className="tr-rl">{p.role}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="tr-prog">
                      <div className={`tr-bar${pct === 100 ? ' tr-green' : ''}`}><i style={{ width: `${pct}%` }} /></div>
                      <span>{p.completedModules} of {p.assignedModules}</span>
                    </div>
                  </td>
                  <td>{p.lastActivity}</td>
                  <td><span className={`tr-badge ${badge.className}`}>{badge.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
